import store from '@/store';
import { ConfigData } from '@/Config';
import useAxios from '@/hooks/useAxios';
import useDevice from '@/hooks/useDevice';
import { FormElementModel, RegistrationApiResponse } from '@/components/addons/registration/types';
import { getCookie, removeCookie, setCookie } from '@/utilities/CookieHelpers';
import { CookieCategory, waitForCookieAccess } from '@/services/cookieConsent';
import { SectionModelType } from '@/typings/types/types';
import { AddonRegistrationModel } from '@/components/addons/registration/Model';
import { FieldType, SectionType } from '@/typings/enums/enums';
import { FormSubmitEvent } from '@/events/formSubmit';
import { FormSubmitResponseEvent } from '@/events/formSubmitResponse';
import useLeadScore from '@/hooks/useLeadscore';
import { getQueryParams } from './Url';
import { sendGameStateEvent } from './iFrameUtils';
import { FormDisableDefaultBehavior } from '@/exceptions/FormDisableDefaultBehavior';
import { ContentType } from '@/components/layout/section/SectionBaseModel';
import { SdkFormSubmitEvent } from '@/sdk/events/formSubmit';
import { sdkEvents } from '@/sdk';
import { ReplacementTags } from '@/store/modules/global/state';

export interface FormCookies {
  [key: string]: string;
}

interface FormattedMetricDataItem {
  [key: string]: string;
}

interface SerializedFormData {
  formData: Record<string, unknown>;
  encrypted: Record<string, unknown>;
}

export const getFormCookieData = (): FormCookies => {
  const campaign = store.getters['campaign/model'];
  return (getCookie('form_' + campaign.id) ?? {}) as FormCookies;
};

/**
 * Get cookie that contains the array of forms remembered.
 *
 * @see addToFormsCookie
 */
const getFormsCookie = (): number[] => {
  const formsCookie = getCookie('form_list');
  return formsCookie ? formsCookie : [];
};

/**
 * Add a form identifier to the form list collection.
 * This is used to keep track of form_x cookies set so that we can
 * do cleanup every now and then.
 */
const addToFormsCookie = (id: number) => {
  let forms = getFormsCookie();
  if (Array.isArray(forms) && !forms.includes(id)) {
    forms.push(id);
  }

  if (forms.length > 3) {
    // Slice last 3 items off the array. These are the ones we want to remember.
    const formsToKeep = forms.slice(-2, 3);

    forms.forEach((form) => {
      if (!formsToKeep.includes(form)) {
        // Remove the cookies from forms that should be forgotten.
        removeCookie('form_' + form);
      }
    });

    forms = formsToKeep;
  }

  setCookie('form_list', JSON.stringify(forms));
};

export const getGlobalFormCookieData = (): FormCookies => {
  const campaign = store.getters['campaign/model'];
  return (getCookie('form_global_' + campaign.state.config?.customerId) ?? {}) as FormCookies;
};

/**
 * Save global form data in a cookie so that it can be retrieved later.
 */
export const saveGlobalFormCookies = async (formFields: FormElementModel[]) => {
  await waitForCookieAccess(CookieCategory.FUNCTIONAL);

  const existingCookie = getGlobalFormCookieData();
  const newCookieData: Record<string, unknown> = {};

  formFields.forEach((field) => {
    const cookieValue = field.getSerializedCookieValue();

    // Global form data is serialized in cookies. For this reason we wouldn't like for it to take up too much space.
    // This is why we exclude form fields with labels that has more than 100 characters in length.
    if (cookieValue.length > 100) {
      return;
    }

    newCookieData[field.state.name] = cookieValue;
  });

  /**
   * Due to size constraints for cookies we need to ensure that our cookie takes up less space than 4096 bytes.
   * This is done by serializing the cookie and measuring it's size. If the size is above our threshold
   * (+ a margin of 200). Then we attempt at shrinking it. The shrinking is done by removing the first item of the
   * existing cookies array until the combined size is less than the desired result.
   */
  const newCookieSize = encodeURIComponent(JSON.stringify({ ...existingCookie, ...newCookieData })).length;

  const campaign = store.getters['campaign/model'];
  if (newCookieSize + 200 >= 4096) {
    let i = 0;

    while (
      encodeURIComponent(JSON.stringify({ ...existingCookie, ...newCookieData })).length + 200 >= 4096 &&
      i < 5000
    ) {
      i++;

      const firstKey = Object.keys(existingCookie)[0];

      if (firstKey) {
        delete existingCookie[`${firstKey}`];
      }
    }

    setCookie(
      'form_global_' + campaign.state.config?.customerId,
      JSON.stringify({ ...existingCookie, ...newCookieData })
    );
  } else {
    setCookie(
      'form_global_' + campaign.state.config?.customerId,
      JSON.stringify({ ...existingCookie, ...newCookieData })
    );
  }
};

/**
 * Save all form data into our form_x cookie so that we can remember previously entered information.
 */
export const saveFormCookies = async (pageModel: SectionModelType): Promise<void> => {
  await waitForCookieAccess(CookieCategory.FUNCTIONAL);
  const campaign = store.getters['campaign/model'];
  const existingCookie = getFormCookieData();

  const cookie = existingCookie;

  pageModel.getAddons<AddonRegistrationModel>('registration').forEach((registrationModel) => {
    registrationModel.state.fields?.forEach((field) => {
      if (!field.state.containsEncryptedValue) {
        cookie[field.id] = field.getSerializedCookieValue();
      }
    });
  });
  setCookie('form_' + campaign.id, JSON.stringify(cookie));
  addToFormsCookie(campaign.id);
};

export const getLocation = (href: string) => {
  // eslint-disable-next-line security/detect-unsafe-regex
  const match = href.match(/^(https?:)\/\/(([^:/?#]*)(?::([0-9]+))?)([/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/);
  return (
    match && {
      href: href,
      protocol: match[1],
      host: match[2],
      hostname: match[3],
      port: match[4],
      pathname: match[5],
      search: match[6],
      hash: match[7]
    }
  );
};

export const getSerializedFormDataForPages = (pages: SectionModelType[]): SerializedFormData => {
  const formData: Record<string, unknown> = {};
  const encrypted: Record<string, unknown> = {};

  pages.forEach((page) => {
    page.getAddons<AddonRegistrationModel>('registration').forEach((registrationModel) => {
      registrationModel.state.fields?.forEach((field) => {
        if (field.state.containsEncryptedValue) {
          encrypted[field.id] = field.getSerializedPostValue();
        } else {
          formData[field.id] = field.getSerializedPostValue();
        }
      });
    });
  });

  return {
    formData,
    encrypted
  };
};

/**
 * Get serialized form data for use in form submit.
 */
export const getSerializedFormData = (page: SectionModelType): SerializedFormData => {
  const campaignState = store.getters['campaign/state'];
  let formData: SerializedFormData = { formData: {}, encrypted: {} };

  if (page.getSectionType() !== SectionType.FLOWPAGE) {
    formData = getSerializedFormDataForPages([page]);
  } else {
    formData = getSerializedFormDataForPages(
      campaignState.flowPages.filter((pageModel) => {
        return pageModel.index <= page.index && pageModel.getAddons<AddonRegistrationModel>('registration').length > 0;
      })
    );
  }

  return formData;
};

export const setFormReplacementTags = () => {
  const replacementTag: ReplacementTags = {};
  const flowRegistrationInfo = store.getters['global/flowRegistrationInfo'];
  store.getters['campaign/model'].formFields.forEach((field) => {
    const replacementValue = field.getStringifiedValue();
    if (typeof replacementValue !== 'undefined') {
      replacementTag[`registration_field_${field.id}`] = replacementValue;
    }
  });
  if (flowRegistrationInfo) {
    replacementTag['registration_id'] = Number(flowRegistrationInfo.id);
  }
  store.dispatch('global/setReplacementTags', replacementTag);
};

export const submitForm = async (pageModel: SectionModelType): Promise<RegistrationApiResponse> => {
  const flowRegistrationInfo = store.getters['global/flowRegistrationInfo'];

  await FormSubmitEvent.emit(pageModel);

  if (!pageModel.state.programmatic) {
    const sdkFormSubmitEvent = new SdkFormSubmitEvent(pageModel);
    if (sdkFormSubmitEvent.formFields.length > 0) {
      sdkEvents.emit('formSubmit', sdkFormSubmitEvent);

      try {
        await sdkFormSubmitEvent.waitForPromises();
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[SDK] Form submit exception caught', e);
        throw new FormDisableDefaultBehavior('Default behavior prevented because of exception in SDK form submit');
      }
    }
  }

  const campaignState = store.getters['campaign/state'];
  const pageIdQuery = `&page=${flowRegistrationInfo?.pages[0] ?? store.getters['campaign/flowId']}`;
  const postUrl = `${
    campaignState.config?.apiEndpoint ?? ConfigData.apiEndpoint
  }/api/v1/campaign/register?campaign_id=${campaignState.id}${store.getters['campaign/flowId'] ? pageIdQuery : ''}${
    campaignState.config?.hash ? '&hash=' + campaignState.config.hash : ''
  }`;

  const metricData = store.getters['global/getMetricData'];
  const isGameWinner = store.getters['global/isGameWinner'];
  const gameData = store.getters['global/getGameData'];
  const instantWinData = store.getters['global/getInstantWinData'];

  const formattedMetricData = Object.keys(metricData).reduce((acc: FormattedMetricDataItem, curr) => {
    acc[`metrics[${curr}]`] = metricData[`${curr}`] + '';
    return acc;
  }, {});

  const { isDesktop, isTablet, isMobile } = useDevice();
  const { formData: serializedFormData, encrypted } = getSerializedFormData(pageModel);

  const params = getQueryParams();

  const formData: Record<string, unknown> = {
    ...store.getters['global/staticFormData'],
    ...serializedFormData,
    ...(Object.keys(encrypted).length > 0 && { encrypted }),
    ...instantWinData,
    ...(gameData && {
      gameData: { ...gameData }
    }),
    mobile: isMobile ? '1' : '0',
    tablet: isTablet ? '1' : '0',
    desktop: isDesktop ? '1' : '0',
    ...(formattedMetricData && { ...formattedMetricData }),
    ...(isGameWinner !== null && { won: isGameWinner ? '1' : '0' }),
    ...(campaignState.config?.leadScoreEnabled && { leadscore: useLeadScore().getTracked() }),
    ...(store.getters['utility/currentUrl'] && { created_from_url: store.getters['utility/currentUrl'] }),
    ...(params && params['foreign-key'] && { foreign_key: params['foreign-key'] })
  };

  const pageItems =
    pageModel.getSectionType() === SectionType.FLOWPAGE
      ? campaignState.flowPages.filter((pageItem) => pageItem.index <= pageModel.index)
      : [pageModel];

  saveFormCookies(pageModel);

  let pages = [];
  const hasRegistrationAddon = pageModel.getAddons('registration').length > 0;
  if (hasRegistrationAddon) {
    pages.push(pageModel.id);
  }

  if (flowRegistrationInfo?.pages) {
    pages = [...flowRegistrationInfo?.pages, ...pages];
  }

  if (pageModel.getSectionType() === SectionType.FLOWPAGE && flowRegistrationInfo && flowRegistrationInfo.id) {
    formData.id = flowRegistrationInfo.id.toString();
    formData.token = flowRegistrationInfo.token;
  } else {
    /**
     * Needs to be cleared so we dont overwrite same registration as their might have been previously
     */
    formData.id = undefined;
  }

  formData.pages = pages;

  if (hasRegistrationAddon && !flowRegistrationInfo?.pages[0]) {
    formData.page = pageModel.id.toString();
  } else if (pages.length > 0) {
    formData.page = pages[pages.length - 1].toString();
  } else {
    formData.page = pageModel.id.toString();
  }

  store.commit('global/setFlowRegistrationInfo', {
    ...(flowRegistrationInfo ? flowRegistrationInfo : {}),
    pages
  });

  if (
    !(
      typeof formData['won'] !== 'undefined' ||
      pageItems.filter((pageItem) => {
        return pageItem.getAddons<AddonRegistrationModel>('registration').some((registrationAddon) => {
          return registrationAddon.state.fields?.some((field) => field.state.type === FieldType.EMAIL);
        });
      }).length > 0
    )
  ) {
    throw new FormDisableDefaultBehavior('Default behavior prevented because of exception in SDK form submit', true);
  }

  if (
    !flowRegistrationInfo &&
    pageModel.getSectionType() === SectionType.FLOWPAGE &&
    // Always allow page containing the actual game to submit, so that results are submitted to the BE.
    pageModel.getAddons('gameplay').length === 0 &&
    pageModel.state.content[0] &&
    // Always allow fictive flow page to submit registration form so that game results are submitted to the BE.
    // This is because some games use fictive flow page as part of the game. So when game ends, it will land here.
    pageModel.state.content[0].type !== ContentType.COMPONENT &&
    !pageModel.getAddons<AddonRegistrationModel>('registration').some((registrationAddon) => {
      return registrationAddon.state.fields?.some((field) => field.state.type === FieldType.EMAIL);
    })
  ) {
    throw new FormDisableDefaultBehavior('Disabled save behavior because form submit was an extra fillout');
  }

  const { postDataFormData } = useAxios<RegistrationApiResponse>(postUrl, formData);
  let response: RegistrationApiResponse | undefined;

  try {
    response = await postDataFormData();
  } catch (e) {
    if (e.response?.data?.errors) {
      await FormSubmitResponseEvent.emit(pageModel, e.response.data);
    }
  }

  if (params['send-game-event'] || (typeof response?.ghostedUser !== 'undefined' && response.ghostedUser === false)) {
    sendGameStateEvent();
  }

  if (response && response.error && response.message === 'game limit reached') {
    await FormSubmitResponseEvent.emit(pageModel, response);
    return response;
  }

  if (response && response.show_message) {
    await FormSubmitResponseEvent.emit(pageModel, response);
    return response;
  }

  // If API responds with replacement tags we should add these.
  if (response && response.replacementtags) {
    await store.dispatch('global/setReplacementTags', response.replacementtags);
  }
  if (response && response.metrics) {
    await store.dispatch('global/setMetricData', response.metrics);
  }

  if (response && response.metrics) {
    await store.dispatch('global/setMetricData', response.metrics);
  }

  if (response && response.object && pageModel.getSectionType() === SectionType.FLOWPAGE) {
    const token = response.object.token;
    const id = response.object.id;

    await store.dispatch('global/setInstantWinData', response.object);

    store.commit('global/setFlowRegistrationInfo', {
      id: Number(id),
      token,
      pages
    });
  }

  if (!response || !response.object) {
    throw new Error('Unrecognized API response');
  }

  await FormSubmitResponseEvent.emit(pageModel, response);

  return response;
};
